import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Logo } from '../common/Logo';
import { useHoliday } from '../../theme/holiday';
import { RailAccount } from './RailAccount';
import { NAV_ITEMS } from './navItems';
import { RailModelLinks, type RailModelContext } from './RailModelLinks';
import { RailRow } from './RailRow';
import { RAIL } from './railTokens';

// ⚠️ MEMBERS HERE ARE NOT ALL ROWS. `model` is a destination with no row at all — a legitimate
// value that lights nothing, because the highlight is `props.nav === item.id` against `NAV_ITEMS`.
// A view whose destination is not a row must still name itself here rather than borrow another
// view's key.
// ⛔ `reggie`, `model_inventory`, `access-expired` and `toolbox` WERE MEMBERS AND ARE FULL-DELETED
// (2026-08-22 / 2026-08-29 R-Q15 / 2026-09-08 R-C3) with the routes they named. `PrdApp`'s
// `NAV_BY_VIEW` is `Record<PrdView['type'], PrdTopNav>`, so a member nothing can produce is a
// compile error to keep, not a harmless leftover.
export type PrdTopNav =
  | 'models' | 'analytics'                       // THE TWO REAL ROWS
  | 'model'                                     // routable, not a row
  | 'clear';                                    // terminal page — lights nothing, deliberately

/**
 * The left navigation rail. It replaces the 56px top bar: global rows, the model level and the one
 * account control.
 *
 * Outside a project the slot is 72px. `engaged` (pointer or focus inside the panel) and `menuOpen`
 * open the panel to 248px as an OVERLAY over the slot, so the page never shifts. There is no collapse
 * button and no pin: the rail opens on hover and focus and closes when they leave.
 *
 * In `projectMode` (handoff step 5) the slot is a 10px strip carrying a gold tick, and the closed
 * panel waits 62px off-screen so 10px of it sits under the strip. Hover or focus slides it in at
 * 248px. The global rows (Dashboard, Models) stay above the model level.
 * ⛔ NO BACK CONTROL — user ruling 2026-10-01 ("there should also be no back button"), overriding the
 * handoff's "Back first". The way out of a project is the page trail (Models › model › project) and
 * the model links here.
 */
export function SideRail(props: {
  nav: PrdTopNav;
  onNavChange: (nav: PrdTopNav) => void;
  // A project route: the rail tucks to the 10px strip.
  projectMode?: boolean;
  // Present only inside a model: opens the model name and links under the Models row.
  modelContext?: RailModelContext;
  onClearProjects?: () => void;
}) {
  const { t } = useTranslation();
  const { hol } = useHoliday();
  const [engaged, setEngaged] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const open = engaged || menuOpen;
  const project = props.projectMode === true;
  const P = RAIL.project;

  return (
    <Box
      component="aside"
      sx={{
        position: 'relative',
        flex: 'none',
        width: project ? P.slotWidth : RAIL.collapsedWidth,
        zIndex: RAIL.slotZIndex,
        transition: RAIL.slotTransition,
      }}
    >
      {project && (
        // The strip draws over the panel's visible 10px. It takes no pointer events, so hovering it is
        // hovering the panel beneath, which is what opens the rail.
        <Box
          data-testid="rail-project-strip"
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: P.slotWidth,
            zIndex: 3,
            pointerEvents: 'none',
            background: P.stripBackground,
            opacity: open ? 0 : 1,
            transition: open ? P.stripOpenTransition : P.stripCloseTransition,
          }}
        />
      )}
      <Box
        data-surface="brand"
        onMouseEnter={() => setEngaged(true)}
        onMouseLeave={() => setEngaged(false)}
        onFocus={() => setEngaged(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setEngaged(false);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: open ? RAIL.openWidth : RAIL.collapsedWidth,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          background: hol.rail ? `${hol.rail}, ${RAIL.panelBackground}` : RAIL.panelBackground,
          borderRight: RAIL.panelBorder,
          ...(project
            ? {
              transform: open ? 'translateX(0)' : `translateX(${P.closedTranslateX}px)`,
              boxShadow: open ? RAIL.shadowOverlay : P.shadowClosed,
              transition: open ? P.openTransition : P.closeTransition,
            }
            : {
              boxShadow: open ? RAIL.shadowOverlay : RAIL.shadowDocked,
              transition: RAIL.panelTransition,
            }),
        }}
      >
        {/* Brand row. The logo is a non-link brand mark. */}
        <Box sx={{ height: RAIL.brandHeight, flex: 'none', display: 'flex', alignItems: 'center', gap: '12px', px: 2, overflow: 'hidden' }}>
          <Box sx={{ width: 40, height: 40, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
            <Logo size={32} />
          </Box>
          <Typography
            component="span"
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: 19,
              letterSpacing: '-0.4px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: open ? 1 : 0,
              maxWidth: open ? 160 : 0,
              transition: RAIL.fadeTransition,
            }}
          >
            {t('nav:header.appName')}
          </Typography>
        </Box>
        <Box sx={{ height: '1px', mx: 2, flex: 'none', background: RAIL.hairline }} />

        <Box
          component="nav"
          aria-label={t('nav:rail.navLabel')}
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '4px', p: '12px', overflow: 'hidden' }}
        >
          {NAV_ITEMS.map(({ id, labelKey, Icon }) => (
            <RailRow
              key={id}
              icon={<Icon />}
              label={t(labelKey)}
              open={open}
              // Inside a model, Models is the PARENT of the page: emphasised, not highlighted.
              active={props.nav === id && !props.modelContext}
              parent={props.nav === id && !!props.modelContext}
              onActivate={() => props.onNavChange(id)}
            />
          ))}
          <RailModelLinks context={props.modelContext} open={open} />
        </Box>

        <Box sx={{ height: '1px', mx: 2, flex: 'none', background: RAIL.hairline }} />
        <RailAccount
          open={open}
          menuOpen={menuOpen}
          onMenuOpenChange={setMenuOpen}
          onClearProjects={props.onClearProjects}
        />
      </Box>
    </Box>
  );
}
